import React, { useState, useEffect } from 'react';
import { Router, Redirect, globalHistory } from '@reach/router';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga';
import { RootState } from '@/redux/reducers';
import { uiActions } from '@/redux/actions/ui';
import { getUserInfoActions } from '@/redux/actions/membership/users';
import { Paths, Pages } from '@/pages/routers';
import OpenDirectoryLayout from '@/layouts/OpenDirectoryLayout/OpenDirectoryLayout';
import AuthLayout from '@/layouts/AuthLayout/AuthLayout';
import DashboardLayout from '@/layouts/DashboardLayout/DashboardLayout';
import env from '@/env';
import { getSubdomain, getWebAppInfo } from '@/utils';
import 'react-toastify/dist/ReactToastify.css';
import '@/assets/styles/root.scss';
import '@/i18n/config';

if (env.ga.mainTrackingCode) {
  ReactGA.initialize(env.ga.mainTrackingCode);
}

globalHistory.listen(({ location }) => {
  ReactGA.pageview(location.pathname);
});

const App = () => {
  const dispatch = useDispatch();
  const authUser = useSelector((state: RootState) => state.authUser.data);
  const [params, setParams] = useState<{ [key: string]: string }>({});
  const subdomain = getSubdomain();
  const hasSubdomain = subdomain && subdomain !== 'www';
  const webApp = getWebAppInfo(env.siteId);
  const getParams = (allParams: { [key: string]: string }) => {
    setParams(allParams);
  };
  const titleMyOrg = `My ${webApp.appName === 'DakkoHub' ? 'Organization' : 'Organisation'}`;

  useEffect(() => {
    const updateSize = () => {
      dispatch(uiActions.setDevice(window.innerWidth));
    };
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [dispatch]);

  useEffect(() => {
    authUser && dispatch(getUserInfoActions.request());
  }, [dispatch, authUser]);

  const [loadRecaptcha, setLoadRecaptcha] = useState<boolean>(false);

  useEffect(() => {
    const loadScriptByURL = (id: string, url: string, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = () => {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };
    // load the script by passing the URL
    if (env.google.recaptchaEnable) {
      loadScriptByURL(
        'recaptcha-key',
        `https://www.google.com/recaptcha/api.js?render=${env.google.recaptchaKey}&render=explicit`,
        () => {
          setLoadRecaptcha(true);
        }
      );
    } else {
      setLoadRecaptcha(true);
    }

    // env.siteId === 'openbankingdirectory' &&
    //   env.cookieControl.apiKey &&
    //   loadScriptByURL('cookieControl', `https://cc.cdn.civiccomputing.com/9/cookieControl-9.5.min.js`, () => {
    //     const config = {
    //       apiKey: env.cookieControl.apiKey,
    //       product: 'COMMUNITY',
    //       position: 'left',
    //       theme: 'LIGHT',
    //       layout: 'slideout',
    //       optionalCookies: [
    //         {
    //           name: 'marketing',
    //           label: 'Marketing Cookies',
    //           description:
    //             'We use marketing cookies to help us improve the relevancy of advertising campaigns you receive.',
    //           cookies: ['fr'],
    //         },
    //       ],
    //     };
    //     const windowParent: any = window;
    //     windowParent?.CookieControl.load(config);
    //   });
  }, []);

  return (
    <>
      {loadRecaptcha && (
        <Router primary={false}>
          <OpenDirectoryLayout path={Paths.Home} params={params}>
            <Pages.Home path={Paths.Home} />
            <Pages.Directory path={Paths.Directory} />
            {/* <Pages.Directory path={Paths.DirectoryWithQuery} getParams={getParams} appParams={params} /> */}
            <Pages.Directory path={Paths.DirectoryWithRegion} getParams={getParams} appParams={params} />
            <Pages.Directory path={Paths.DirectoryWithCountry} getParams={getParams} appParams={params} />
            <Pages.Directory path={Paths.DirectoryWithTag} getParams={getParams} appParams={params} />
            <Pages.Directory path={Paths.DirectoryWithFeature} getParams={getParams} appParams={params} />
            <Pages.EntityDetail path={Paths.EntityDetail} getParams={getParams} appParams={params} />
            <Pages.EntityApis path={Paths.EntityApis} getParams={getParams} appParams={params} />
            <Pages.EntityShowcase path={Paths.EntityShowcase} getParams={getParams} appParams={params} />
            <Pages.EntityService path={Paths.EntityService} getParams={getParams} appParams={params} />
            <Pages.Compare path={Paths.Compare} />
            <Pages.Compare path={Paths.CompareWithCountry} getParams={getParams} appParams={params} />
            <Pages.ArticleDetail path={Paths.ArticleDetail} />
            <Pages.ArticleDetail path={Paths.OldArticleDetail} />
            <Pages.LegalDocument path={Paths.TermsOfUse} />
            <Pages.LegalDocument path={Paths.AboutUs} />
            <Pages.LegalDocument path={Paths.PrivacyPolicy} />
            <Pages.ContactUs path={Paths.ContactUs} />
            <Pages.FintechMap path={Paths.FintechMap} />
            <Pages.News path={Paths.News} />
            <Pages.News path={Paths.ArticleWithTags} />
            <Pages.Standards path={Paths.Standards} />
            <Pages.SocialLinkedin path={Paths.SocialLinkedin} />
            <Pages.NotFound404Page path={Paths.NotFound404Page} />
            <Redirect noThrow from={Paths.Rest} to={Paths.NotFound404Page} />
          </OpenDirectoryLayout>
          <AuthLayout path={Paths.Auth}>
            <Pages.Register path={Paths.Register} />
            <Pages.BankDetail path={Paths.BankDetail} />
            <Pages.Login path={Paths.Login} />
            <Pages.ForgotPassword path={Paths.ForgotPassword} />
            <Pages.ActivateAccount path={Paths.ActivateAccount} />
            <Pages.RegisterSuccess path={Paths.RegisterSuccess} />
            <Pages.ResetPassword path={Paths.ResetPassword} />
            <Redirect noThrow from={Paths.Rest} to={Paths.Home} />
          </AuthLayout>
          {hasSubdomain ? (
            <DashboardLayout path={Paths.Dashboard}>
              <Pages.MyDirectory path={Paths.MyDirectory} />
              <Pages.MyOrganisation path={Paths.MyOrganisation} title={titleMyOrg} />
              <Pages.MyApps path={Paths.MyApps} />
              <Pages.MyAccount path={Paths.MyAccount} />
              <Pages.CreateMyApp path={Paths.CreateMyApp} />
              <Pages.EditMyApp path={Paths.EditMyApp} />
              <Pages.MyPortal path={Paths.MyPortal} />
              <Pages.MyServices path={Paths.MyServices} />
              <Pages.AddService path={Paths.AddService} />
              <Pages.EditService path={Paths.EditService} />
              <Redirect noThrow from={Paths.Rest} to={`${Paths.Dashboard}${Paths.MyOrganisation}`} />
            </DashboardLayout>
          ) : (
            <DashboardLayout path={Paths.Dashboard}>
              <Pages.MyDirectory path={Paths.MyDirectory} />
              <Pages.MyAccount path={Paths.MyAccount} />
              <Pages.AddEntity path={Paths.AddEntity} />
              <Pages.MyArticles path={Paths.MyArticles} />
              <Pages.AddArticle path={Paths.AddArticle} />
              <Pages.EditArticle path={Paths.EditArticle} />
              <Pages.SocialUser path={Paths.SocialUser} />
              <Pages.ApiCategories path={Paths.ApiCategories} />
              <Pages.AddApiCategory path={Paths.AddApiCategory} />
              <Pages.EditApiCategory path={Paths.EditApiCategory} />
              <Pages.AppCategories path={Paths.AppCategories} />
              <Pages.AddAppCategory path={Paths.AddAppCategory} />
              <Pages.EditAppCategory path={Paths.EditAppCategory} />
              <Pages.ServiceCategories path={Paths.ServiceCategories} />
              <Pages.AddServiceCategory path={Paths.AddServiceCategory} />
              <Pages.EditServiceCategory path={Paths.EditServiceCategory} />
              <Redirect noThrow from={Paths.Rest} to={`${Paths.Dashboard}${Paths.MyDirectory}`} />
            </DashboardLayout>
          )}
        </Router>
      )}
    </>
  );
};

export default App;
